import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor, type BadgeColor } from "@/components/DataTable";
import { CheckCircle2, XCircle, Clock, Send } from "lucide-react";

type StatusInfo = {
  status: string;
  badge: BadgeColor;
  icon: React.ReactNode;
  short: string;
  description: string;
  next: string[];
};

/**
 * Statuts PayDRC réels de la table `drc_send_money_transac` :
 * Pending → Submitted → Successful | Failed.
 * Le dashboard les affiche en lowercase.
 */
const STATUSES: StatusInfo[] = [
  {
    status: "pending",
    badge: "warning",
    icon: <Clock size={16} />,
    short: "Paiement initié, en attente de l'action client",
    description:
      "Moko a reçu votre appel API (ou l'utilisateur a cliqué payer sur votre checkout). Le client doit maintenant valider sur son téléphone : saisir son code PIN Mobile Money (M-Pesa, Orange Money, Airtel Money) pour autoriser le débit.",
    next: ["submitted", "failed"],
  },
  {
    status: "submitted",
    badge: "info",
    icon: <Send size={16} />,
    short: "Soumis au telco, en attente de confirmation",
    description:
      "Le client a validé côté téléphone. Moko a transmis la demande au telco (Vodacom, Orange, Airtel, Africell) et attend le retour final. Aucune action requise — le statut basculera en `successful` ou `failed` dès que le telco confirmera, généralement en quelques secondes.",
    next: ["successful", "failed"],
  },
  {
    status: "successful",
    badge: "success",
    icon: <CheckCircle2 size={16} />,
    short: "Paiement confirmé — fonds crédités sur votre wallet",
    description:
      "Le telco a confirmé le débit du client. Les fonds sont crédités sur votre wallet PayDRC (net de la commission Moko). Un webhook est envoyé à votre callback URL configuré dans Paramètres. La référence Telco (Réf. Telco dans la liste des paiements) est renseignée.",
    next: [],
  },
  {
    status: "failed",
    badge: "danger",
    icon: <XCircle size={16} />,
    short: "Échec — client non débité",
    description:
      "Le telco a renvoyé un échec définitif. Aucun débit côté client. La cause précise est dans le champ description du paiement (ex : solde insuffisant, mauvais PIN, wallet suspendu, timeout côté telco). Un webhook `failed` est envoyé à votre callback URL.",
    next: [],
  },
];

export function TransactionStatusesPage() {
  return (
    <div>
      <PageHeader
        title="Statuts des transactions"
        breadcrumbs={[{ label: "Transactions" }, { label: "Statuts" }]}
      />

      <div className="card mb-6">
        <div className="card-body">
          <div className="text-sm text-[#3f4254] leading-relaxed">
            Chaque paiement PayDRC traverse plusieurs statuts entre l'initiation et la confirmation
            finale. Voici la signification de chaque statut affiché dans la liste des paiements, et
            les transitions possibles.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {STATUSES.map((s) => (
          <div key={s.status} className="card">
            <div className="card-body">
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg(s.badge)}`}>
                  {s.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge color={s.badge}>{s.status}</Badge>
                  </div>
                  <div className="font-semibold text-[#181c32]">{s.short}</div>
                </div>
              </div>
              <div className="text-sm text-[#3f4254] leading-relaxed">{s.description}</div>
              {s.next.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#eff2f5]">
                  <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-2">
                    Transitions possibles
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.next.map((n) => (
                      <Badge key={n} color={statusColor(n)}>
                        → {n}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card mt-6">
        <div className="card-header">
          <div className="card-title">Statuts terminaux vs intermédiaires</div>
        </div>
        <div className="card-body space-y-3 text-sm text-[#3f4254] leading-relaxed">
          <p>
            <strong>Statuts terminaux</strong> (le paiement n'évolue plus) :{" "}
            <Badge color="success">successful</Badge> <Badge color="danger">failed</Badge>
          </p>
          <p>
            <strong>Statuts intermédiaires</strong> (le paiement va basculer) :{" "}
            <Badge color="warning">pending</Badge> <Badge color="info">submitted</Badge> —
            un paiement ne reste pas indéfiniment dans ces états. Si aucune réponse du telco n'arrive,
            il bascule automatiquement en <Badge color="danger">failed</Badge> après un délai
            d'expiration côté opérateur.
          </p>
        </div>
      </div>

      <div className="card mt-6">
        <div className="card-header">
          <div className="card-title">Bonnes pratiques d'intégration</div>
        </div>
        <div className="card-body space-y-3 text-sm text-[#3f4254] leading-relaxed">
          <p>
            <strong>Attendez le callback</strong> — ne considérez jamais un paiement comme confirmé
            tant que vous n'avez pas reçu le webhook <Badge color="success">successful</Badge> sur
            votre callback URL. Les statuts <Badge color="warning">pending</Badge> et{" "}
            <Badge color="info">submitted</Badge> ne garantissent pas la finalisation.
          </p>
          <p>
            <strong>Vérifiez la référence Telco</strong> — la colonne <em>Réf. Telco</em> de la
            liste des paiements contient la référence unique du telco (ex : <code>MP260824.1247.F34217</code>{" "}
            pour M-Pesa). C'est cette référence qui fait foi en cas de réconciliation avec Vodacom /
            Orange / Airtel.
          </p>
          <p>
            <strong>Idempotence côté vous</strong> — utilisez votre <em>Réf. Marchand</em>{" "}
            (<code>thirdparty_reference</code>) comme clé unique dans votre système pour éviter
            les doubles créditations si un webhook est reçu plusieurs fois.
          </p>
        </div>
      </div>
    </div>
  );
}

function iconBg(c: BadgeColor): string {
  return {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    danger: "bg-danger-50 text-danger-700",
    warning: "bg-warning-50 text-warning-700",
    info: "bg-info-50 text-info-700",
    light: "bg-[#f5f8fa] text-[#5e6278]",
  }[c];
}
